import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTicket } from "../../store/Store";

const TicketHeader = () => {
  const { t } = useTranslation();
  const { data: ticketData } = useTicket();

  const {
    resource: { status, uuid, ticket_created_at, client, company } = {},
  } = ticketData || {};

  const ticketHeaderTitle = useMemo(() => {
    if (company?.display_invoice_title_on_widget) {
      return `${t("ticket.invoice")} `;
    }
    return `${t("ticket.title")} `;
  }, [company, t]);

  const statusColor = useMemo(() => {
    if (status === 0) return "bg-yellow-100 text-yellow-800";
    if (status === 1) return "bg-green-100 text-green-800";
    return "bg-gray-100 text-black";
  }, [status]);

  const statusLabel = useMemo(() => {
    if (status === 0) return t("ticket.pending");
    if (status === 1) return t("ticket.completed");
    if (status === 2) return t("ticket.voided");
    if (status === 3) return t("ticket.reopened");
    return t("ticket.unknown");
  }, [status, t]);

  const formattedDate = useMemo(() => {
    if (!ticket_created_at) return "";
    return new Date(ticket_created_at).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [ticket_created_at]);

  const clientName = useMemo(() => {
    if (!client?.first_name && !client?.last_name) {
      return null;
    }
    return `${client.first_name || ""} ${client.last_name || ""}`.trim();
  }, [client]);

  const clientAddress = useMemo(() => {
    if (!client?.addresses?.length) return null;
    return client.addresses[0];
  }, [client]);

  return (
    <div className="w-full flex flex-col gap-4" data-testid="ticket-header">
      <div>
        {/* Ticket Info */}
        <div className="md:flex-1 md:min-w-[245px]">
          <div className="flex gap-4 items-center">
            <div>
              <h2
                className="text-lg font-medium text-black"
                data-testid="ticket-title"
              >
                {ticketHeaderTitle}
                <span data-testid="ticket-number">#{uuid}</span>
              </h2>
            </div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}
              data-testid="ticket-status"
            >
              {statusLabel}
            </span>
          </div>

          {formattedDate && (
            <div className="mt-2">
              <div className="flex gap-2 items-center">
                <p className="text-sm text-black" data-testid="ticket-date">
                  {formattedDate}
                </p>
              </div>
            </div>
          )}

          {clientName && (
            <div className="mt-2">
              <div className="flex gap-2 items-center">
                <p className="text-sm text-black font-semibold">
                  {t("ticket.client")}:
                </p>
                <span className="text-sm text-black" data-testid="client-name">
                  {clientName}
                </span>
              </div>
              {clientAddress && (
                <div
                  className="flex flex-col min-[920px]:flex-row text-sm text-black-light mt-1"
                  data-testid="client-address"
                >
                  <div>
                    {clientAddress.street_address}
                    {clientAddress.street_address_2
                      ? `, ${clientAddress.street_address_2}`
                      : ""}
                    ,
                  </div>
                  <div className="min-[920px]:pl-1">
                    {clientAddress.city}, {clientAddress.state}{" "}
                    {clientAddress.zip}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketHeader;
